import React from 'react';
import { Container, Box, Typography } from '@mui/material';

interface TimelineStep {
  number: number;
  title: string;
  description: React.ReactNode;
  isActive?: boolean;
}

interface InstructionsTimelineProps {
  title: string;
  steps: TimelineStep[];
  imageSrc: string;
  imageAlt: string;
}

const InstructionsTimeline: React.FC<InstructionsTimelineProps> = ({ title, steps, imageSrc, imageAlt }) => {
  return (
    <Container maxWidth="lg" sx={{ my: 8 }}>
      <Typography variant="h4" component="h2" textAlign="center" gutterBottom>
        {title}
      </Typography>
      <Box sx={{ 
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 4,
        alignItems: { xs: 'center', md: 'flex-start' }
      }}>
        <Box sx={{ 
          position: 'relative',
          maxWidth: '600px',
          flex: 1,
          margin: '0 auto',
          width: '100%',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: { xs: '18px', md: '24px' },
            top: '0',
            height: `calc(100% - ${steps.length === 1 ? '48px' : '96px'})`,
            width: '2px',
            backgroundColor: '#e0e0e0',
          }
        }}>
          {steps.map((step, index) => (
            <Box key={index} sx={{ 
              display: 'flex', 
              gap: { xs: 2, md: 4 },
              mb: index !== steps.length - 1 ? { xs: 3, md: 5 } : 0,
              position: 'relative' 
            }}>
              <Box sx={{ 
                width: { xs: '36px', md: '48px' },
                height: { xs: '36px', md: '48px' },
                borderRadius: '50%',
                backgroundColor: '#e0e0e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                zIndex: 1,
                border: { xs: '4px solid #f5f5f5', md: '6px solid #f5f5f5' }
              }}>
                <Typography sx={{ 
                  color: 'text.secondary', 
                  fontWeight: 'bold',
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }}>
                  {step.number}
                </Typography>
              </Box>
              <Box>
                <Typography 
                  variant="h5" 
                  component="h3" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 'bold',
                    color: 'inherit',
                    fontSize: { xs: '1.125rem', md: '1.5rem' }
                  }}
                >
                  {step.title}
                </Typography>
                <Typography 
                  variant="body1" 
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.875rem', md: '1rem' }
                  }}
                >
                  {step.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
        
        <Box sx={{ 
          flex: 1,
          maxWidth: { xs: '100%', md: '500px' }
        }}>
          <img 
            src={imageSrc}
            alt={imageAlt}
            style={{
              width: '100%',
              height: 'auto',
              borderRadius: '8px'
            }}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default InstructionsTimeline; 